import { useMemo, useState } from "react";
import { Wrench, Ruler, Weight, CheckCircle2 } from "lucide-react";
import { useLang } from "../context/LanguageContext";

/**
 * Returns a hinge spec recommendation based on industry-standard load rules.
 * Weight rule (per leaf):
 *   ≤ 9 kg → 2 hinges
 *   9–18 kg → 3 hinges
 *   18–27 kg → 4 hinges
 *   27–36 kg → 5 hinges
 *   > 36 kg → 6 hinges
 * Height adjustment:
 *   > 1700 mm → +1 hinge
 *   > 2000 mm → +1 more hinge
 */
function recommend(weightKg, heightMm) {
  const w = Number(weightKg) || 0;
  const h = Number(heightMm) || 0;

  let count;
  if (w <= 0) count = 2;
  else if (w <= 9) count = 2;
  else if (w <= 18) count = 3;
  else if (w <= 27) count = 4;
  else if (w <= 36) count = 5;
  else count = 6;

  if (h > 2000) count += 2;
  else if (h > 1700) count += 1;

  // Choose model key
  let modelKey;
  if (w <= 10) modelKey = "softCloseStandard";
  else if (w <= 25) modelKey = "softCloseHeavy";
  else modelKey = "industrialTwoWay";

  return { count: Math.max(2, Math.min(count, 8)), modelKey };
}

export default function HingeLoadCalculator() {
  const { t } = useLang();
  const c = t.ikelPage.hingeCalc;

  const [weight, setWeight] = useState(18);
  const [height, setHeight] = useState(720);

  const rec = useMemo(() => recommend(weight, height), [weight, height]);
  const model = c.models[rec.modelKey];

  return (
    <section
      className="py-24 md:py-32 bg-white border-t border-[#E2E8F0]"
      data-testid="hinge-calculator-section"
    >
      <div className="pgb-container">
        <div className="max-w-3xl mb-12">
          <span className="eyebrow text-[#F59E0B]">{c.eyebrow}</span>
          <h2 className="mt-4 text-3xl md:text-5xl font-black tracking-tighter text-[#0B1B29]">
            {c.title}
          </h2>
          <p className="mt-5 text-[#536878] leading-relaxed max-w-2xl">
            {c.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Inputs */}
          <div
            className="lg:col-span-5 bg-[#F5F5F7] border border-[#E2E8F0] p-6 md:p-8"
            data-testid="hinge-inputs"
          >
            <div className="space-y-6">
              <div>
                <label
                  htmlFor="hinge-weight"
                  className="flex items-center gap-2 text-[11px] font-bold tracking-[0.18em] uppercase text-[#536878]"
                >
                  <Weight size={14} /> {c.fields.weight}
                </label>
                <input
                  id="hinge-weight"
                  type="number"
                  min="0"
                  step="0.5"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  className="hinge-input mt-2"
                  data-testid="hinge-weight"
                />
                <input
                  type="range"
                  min="0"
                  max="60"
                  step="0.5"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  className="w-full mt-3 accent-[#F59E0B]"
                  data-testid="hinge-weight-slider"
                />
                <div className="flex justify-between text-[10px] font-mono text-[#536878] mt-1">
                  <span>0 kg</span>
                  <span>60 kg</span>
                </div>
              </div>

              <div>
                <label
                  htmlFor="hinge-height"
                  className="flex items-center gap-2 text-[11px] font-bold tracking-[0.18em] uppercase text-[#536878]"
                >
                  <Ruler size={14} /> {c.fields.height}
                </label>
                <input
                  id="hinge-height"
                  type="number"
                  min="0"
                  step="10"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  className="hinge-input mt-2"
                  data-testid="hinge-height"
                />
                <input
                  type="range"
                  min="200"
                  max="2400"
                  step="10"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  className="w-full mt-3 accent-[#F59E0B]"
                  data-testid="hinge-height-slider"
                />
                <div className="flex justify-between text-[10px] font-mono text-[#536878] mt-1">
                  <span>200 mm</span>
                  <span>2400 mm</span>
                </div>
              </div>

              <p className="text-[11px] text-[#536878] leading-relaxed">
                {c.disclaimer}
              </p>
            </div>
          </div>

          {/* Result */}
          <div
            className="lg:col-span-7 bg-[#0B1B29] text-white p-6 md:p-8 relative overflow-hidden"
            data-testid="hinge-result"
          >
            <div
              aria-hidden="true"
              className="absolute -top-24 -right-24 w-[360px] h-[360px] bg-[#F59E0B] blur-[140px] opacity-25"
            />
            <div className="relative">
              <span className="eyebrow text-[#F59E0B]">{c.resultTitle}</span>

              <div className="mt-6 flex items-start gap-6">
                <div className="flex-shrink-0 w-24 h-24 bg-[#F59E0B] text-[#0B1B29] flex flex-col items-center justify-center">
                  <div
                    className="text-5xl font-black leading-none tracking-tighter"
                    style={{ fontFamily: "Chivo" }}
                    data-testid="hinge-count"
                  >
                    {rec.count}
                  </div>
                  <div className="text-[9px] tracking-[0.18em] uppercase mt-1 font-bold">
                    {c.hinges}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] tracking-[0.22em] uppercase text-[#93C5FD] mb-2">
                    {c.recommendedModel}
                  </div>
                  <h3
                    className="text-2xl md:text-3xl font-black tracking-tight leading-tight"
                    data-testid="hinge-model"
                  >
                    {model.name}
                  </h3>
                  <p className="mt-2 text-sm text-white/75 leading-relaxed">
                    {model.desc}
                  </p>
                </div>
              </div>

              <ul className="mt-8 space-y-2 text-sm" data-testid="hinge-specs">
                {model.specs.map((s) => (
                  <li key={s} className="flex items-start gap-2 text-white/85">
                    <CheckCircle2 size={16} className="text-[#22C55E] flex-shrink-0 mt-0.5" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 pt-6 border-t border-white/15 flex flex-wrap items-center gap-3">
                <Wrench size={16} className="text-[#F59E0B]" />
                <span className="text-xs text-white/70 tracking-wide">
                  {c.ctaHelper}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .hinge-input {
          width: 100%;
          padding: 12px 14px;
          background: #ffffff;
          border: 1px solid #E2E8F0;
          font-size: 16px;
          font-weight: 700;
          color: #0B1B29;
          font-family: 'Chivo', sans-serif;
          outline: none;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .hinge-input:focus {
          border-color: #F59E0B;
          box-shadow: 0 0 0 3px rgba(245,158,11,0.2);
        }
      `}</style>
    </section>
  );
}
